<template>
  <div class="checklist-viewer-wrapper">
    <div class="checklist-viewer__actions" v-if="hasChecklist">
      <b-button
        variant="link"
        size="sm"
        class="checklist-viewer__expand"
        title="Abrir checklist en una vista ampliada"
        aria-label="Abrir checklist en una vista ampliada"
        @click="isExpanded = true"
      >
        <AppIcon name="open_in_full" />
        <span>Ampliar</span>
      </b-button>
    </div>

    <div
      ref="viewer"
      class="checklist-viewer"
      :class="{ 'checklist-viewer--readonly': !editable }"
      v-html="value"
      @click="onClick($event)"
      @mouseover="onMouseOver($event)"
      @mouseout="onMouseOut($event)"
    ></div>

    <b-modal v-model="isExpanded" :title="title || 'Checklist'" size="lg" centered hide-footer @shown="decorateCopyButtons">
      <div
        ref="modalViewer"
        class="checklist-viewer checklist-viewer--modal"
        :class="{ 'checklist-viewer--readonly': !editable }"
        v-html="value"
        @click="onClick($event)"
        @mouseover="onMouseOver($event)"
        @mouseout="onMouseOut($event)"
      ></div>
    </b-modal>
  </div>
</template>

<script>
import ChecklistUtils from './checklistUtils'

// Renderiza HTML de un editor rico (vue2-editor/Quill) y, si trae
// checklists ([] tildables), permite tildar/destildar haciendo click sin
// abrir el editor. No persiste nada por su cuenta -- emite `input` con el
// html actualizado y el consumidor decide cómo guardarlo (cada módulo
// tiene su propio service/endpoint).
export default {
  name: 'ChecklistViewer',
  props: {
    value: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isExpanded: false,
    }
  },
  computed: {
    hasChecklist() {
      return /<ul[^>]*\bdata-checked(?:\s|=|>)/i.test(this.value || '')
    },
  },
  mounted() {
    this.decorateCopyButtons()
  },
  updated() {
    this.$nextTick(this.decorateCopyButtons)
  },
  methods: {
    onClick(event) {
      var copyButton = event.target.closest('.checklist-viewer__copy')
      if (copyButton) {
        event.preventDefault()
        event.stopPropagation()
        this.copyItem(copyButton.closest('li'), copyButton)
        return
      }

      if (!this.editable) return

      var li = event.target.closest('li')
      if (!li) return

      var ul = li.parentElement
      if (!ul || ul.tagName !== 'UL' || !ul.hasAttribute('data-checked')) return

      var allItems = Array.prototype.slice.call(event.currentTarget.querySelectorAll('ul[data-checked] > li'))
      var index = allItems.indexOf(li)
      if (index === -1) return

      var updated = ChecklistUtils.toggleAt(this.value, index)
      if (updated !== null) this.$emit('input', updated)
    },
    onMouseOver(event) {
      var button = event.target.closest('.checklist-viewer__copy')
      if (!button || (event.relatedTarget && button.contains(event.relatedTarget))) return
      var item = button.closest('li')
      if (item) item.classList.add('checklist-viewer__item--copy-hover')
    },
    onMouseOut(event) {
      var button = event.target.closest('.checklist-viewer__copy')
      if (!button || (event.relatedTarget && button.contains(event.relatedTarget))) return
      var item = button.closest('li')
      if (item) item.classList.remove('checklist-viewer__item--copy-hover')
    },
    decorateCopyButtons() {
      var viewers = [this.$refs.viewer, this.$refs.modalViewer]
      viewers.filter(Boolean).forEach(viewer => {
        viewer.querySelectorAll('ul[data-checked] > li').forEach(item => {
          var hasCopyControl = Array.prototype.some.call(item.children, child => child.classList.contains('checklist-viewer__copy'))
          if (hasCopyControl) return

          var button = document.createElement('button')
          button.type = 'button'
          button.className = 'checklist-viewer__copy'
          button.setAttribute('title', 'Copiar ítem al portapapeles')
          button.setAttribute('aria-label', 'Copiar ítem al portapapeles')
          button.innerHTML = '<span class="material-symbols-rounded app-icon" aria-hidden="true">content_copy</span>'
          item.appendChild(button)
        })
      })
    },
    copyItem(item, button) {
      if (!item) return

      var clone = item.cloneNode(true)
      var copyControl = clone.querySelector('.checklist-viewer__copy')
      if (copyControl) copyControl.remove()
      var text = (clone.textContent || '').replace(/\s+/g, ' ').trim()
      if (!text) return

      this.writeToClipboard(text).then(() => {
        button.classList.add('checklist-viewer__copy--copied')
        window.setTimeout(() => button.classList.remove('checklist-viewer__copy--copied'), 900)
      })
    },
    writeToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text)

      return new Promise((resolve, reject) => {
        var textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        var copied = document.execCommand('copy')
        textarea.remove()
        if (copied) resolve()
        else reject(new Error('No se pudo copiar el ítem'))
      })
    },
  },
}
</script>

<style>
.checklist-viewer ul[data-checked] {
  margin: 0;
  list-style: none;
  padding-left: 1.8em;
}
.checklist-viewer ul[data-checked] > li {
  position: relative;
  padding: 1px 34px 1px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  transition: background-color .15s ease;
}
.checklist-viewer--readonly ul[data-checked] > li {
  cursor: default;
}
.checklist-viewer:not(.checklist-viewer--readonly) ul[data-checked] > li:hover {
  background: var(--app-color-surface);
}
.checklist-viewer ul[data-checked] > li.checklist-viewer__item--copy-hover {
  background: var(--app-color-primary-soft);
}
.checklist-viewer ul[data-checked] > li::before {
  display: inline-block;
  width: 1.4em;
  margin-left: -1.8em;
  margin-right: 0.4em;
  text-align: right;
  font-size: 1.3em;
  font-weight: 900;
  color: var(--app-color-text-muted);
  vertical-align: -0.05em;
}
.checklist-viewer ul[data-checked="false"] > li::before {
  content: '\2610';
}
.checklist-viewer ul[data-checked="true"] > li::before {
  content: '\2611';
  color: var(--app-color-success);
}
.checklist-viewer ul[data-checked="true"] > li {
  color: var(--app-color-text-muted);
  text-decoration: line-through;
}
.checklist-viewer__copy {
  position: absolute;
  top: 50%;
  right: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--app-color-text-muted);
  cursor: pointer;
  opacity: .55;
  text-decoration: none;
  transform: translateY(-50%);
  transition: background-color .15s ease, color .15s ease, opacity .2s ease;
}
.checklist-viewer__copy .app-icon {
  font-size: 15px;
}
.checklist-viewer__copy:hover,
.checklist-viewer__copy:focus {
  background: var(--app-color-primary-soft-hover);
  color: var(--app-color-primary);
  opacity: 1;
  outline: 0;
}
.checklist-viewer__copy--copied {
  background: var(--app-color-success-soft);
  color: var(--app-color-success);
  opacity: 1;
  animation: checklist-copy-feedback .25s ease-out;
}
@keyframes checklist-copy-feedback {
  50% { transform: translateY(-50%) scale(.82); }
}
.checklist-viewer hr {
  margin-top: 7px;
  margin-bottom: 7px;
}
.checklist-viewer__actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}
.checklist-viewer__expand {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  color: var(--app-color-primary);
  font-size: 12px;
  text-decoration: none;
}
.checklist-viewer__expand:hover,
.checklist-viewer__expand:focus {
  text-decoration: underline;
}
.checklist-viewer--modal {
  min-height: 240px;
  padding: 4px 6px;
}
.checklist-viewer--modal ul[data-checked] > li {
  padding-top: 2px;
  padding-bottom: 2px;
  font-size: 16px;
}
.checklist-viewer--modal ul[data-checked] > li.checklist-viewer__item--copy-hover {
  background: var(--app-color-primary-soft-hover) !important;
  box-shadow: inset 0 0 0 1px var(--app-color-primary-border);
}
.checklist-viewer--modal ul[data-checked] > li:has(.checklist-viewer__copy:hover) {
  background: var(--app-color-primary-soft-hover) !important;
  box-shadow: inset 0 0 0 1px var(--app-color-primary-border);
}
</style>
